import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 8.3 visual preview at /preview/lesson/8.3
 * "Drift: staying sharp on purpose"
 * The quiet failure mode of competent AI users, and the re-prove move.
 */

export default function LessonPreviewPage8_3() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromPrev />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 8 · AI in a world where it has impacted everything · Lesson 8.3
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Drift: staying sharp on purpose
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Habits · Atrophy · Re-prove</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 drift-spotting drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="The failure mode is not over-trusting AI. The failure mode is slowly forgetting you can do the work without it.">
      <p className="mt-6 text-text/90">
        Six months in, you have a rhythm. You open the AI. You describe the
        job. You read the draft. You edit. You ship. The work goes out the
        door. People say it is good. By any visible measure, you are doing
        fine.
      </p>
      <p className="mt-4 text-text/90">
        Then one Tuesday the AI is down. Or your subscription expired. Or
        you are in a meeting and the laptop is closed and someone asks you
        to explain, on the spot, the brief you sent last week. And you
        realize you do not actually remember what you decided. You remember
        that the draft looked good.
      </p>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The quiet version of failure
        </p>
        <p className="mt-3 text-text/90">
          The dangerous version is not the loud one where someone trusts a
          hallucination and ships it. That happens once and you learn.
          The dangerous version is the quiet one where the work keeps
          shipping and you keep nodding along and your judgment is slowly
          getting weaker without anyone, including you, noticing.
        </p>
      </div>

      <p className="mt-6 text-text/90">
        That is drift. It is not a moral failing. It is what happens to any
        skill you stop practicing. AI is good enough at the mechanical parts
        that you can let go of them, and most people do, and they do not
        feel it until something forces them to do the work alone.
      </p>
      <p className="mt-4 text-text/90">
        Lesson 8.3 is about naming the drift signals, catching them on
        yourself, and the one weekly move that keeps your judgment from
        going soft: re-proving the basics, on purpose, on one real task.
      </p>
    </Section>
  );
}

/* --- Section 2: From 8.2 --- */
function FromPrev() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Lesson 8.2" title="8.2 named your anchors. 8.3 is what protects them from quiet erosion.">
      <p className="mt-6 text-text/90">
        In 8.2 you named your three anchors: judgment, standing, relationship.
        Those are the parts of your work AI cannot do for you. They sound
        durable. They are not, automatically. They are durable the way a
        muscle is durable: only if you keep using them.
      </p>
      <p className="mt-4 text-text/90">
        Drift is what happens to anchors that go unused. The judgment muscle
        you do not exercise stops being sharp. The standing you do not test
        stops being defended. The relationship you only handle through
        AI-drafted messages stops being a relationship. Lesson 8.3 is the
        weekly practice that keeps your anchors honest.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="One sentence on what an anchor is." />
        <ReviewItem n="02" q="Why anchors do not stay sharp on their own." />
        <ReviewItem n="03" q="Name your three anchors in one breath each." />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · Drift and re-prove" title="Five drift signals to catch on yourself, and one weekly move that keeps your judgment sharp.">
      <Subhead n={1} title="The vocab for the lesson" />
      <VocabCard
        term="Drift"
        definition="Drift is the quiet erosion of your judgment that happens when the AI gets good enough that you slowly stop doing the human parts of your own work. You still ship. You still get credit. But the muscle that produced the work is somebody else's, and the longer it goes on the harder it is to notice."
        analogy="Drift is GPS for your skills. If you use GPS to drive somewhere once, you still know how to find it. If you use GPS every day for a year, you can live in your own city and not know how to get to the grocery store without it. Nothing dramatic happened. You just stopped doing the work of knowing, because the tool was always there. Then one day the phone dies and you cannot find the store."
        memoryHook="Drift is when the work still ships but you cannot defend it without the tool open. If you can only do the work with AI, you are drifting. If you can do it without AI when you have to, the AI is making you faster, not weaker."
        why="Drift is the most common, least visible failure mode of competent AI users. It does not show up in your output. It does not get caught by your manager. It gets caught when something forces you to work without the AI, and by then you have to relearn things you used to know. The re-prove move is cheap insurance. One task a week, done without AI on purpose. That is the difference between a career where AI makes you stronger and one where it slowly hollows you out."
      />

      <Subhead n={2} title="The five drift signals" className="mt-12" />
      <p className="mt-4 text-text/90">
        Drift is hard to see because the work keeps shipping. These five
        signals are the early warnings. None of them is proof on its own.
        Two or three together is the read.
      </p>
      <SignalsGrid />

      <Subhead n={3} title="The re-prove move" className="mt-12" />
      <p className="mt-4 text-text/90">
        Once a week, take one real task you would normally do with AI and do
        it without. Not as nostalgia. Not as a purity test. As proof to
        yourself that the judgment behind the work is still yours, with or
        without the tool.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          How to run one re-prove session
        </p>
        <ol className="mt-3 space-y-3 text-text/90">
          <li><span className="font-mono text-xs text-amber mr-2">01.</span> Pick a task you would normally hand to AI first. A short brief, a section of a doc, an email that matters, a small piece of code.</li>
          <li><span className="font-mono text-xs text-amber mr-2">02.</span> Set a clock. Do the work alone. No chat, no search assist, no autocomplete on anything bigger than a word.</li>
          <li><span className="font-mono text-xs text-amber mr-2">03.</span> Ship the no-AI version, or shelf it. Then run the same task with AI and compare. Where was the AI version sharper? Where was yours? Which version are you proud of?</li>
          <li><span className="font-mono text-xs text-amber mr-2">04.</span> Write one sentence in your operator notes: what you noticed. That sentence is the audit trail. It tells you, six months from now, whether you have actually been re-proving or just thinking about it.</li>
        </ol>
      </div>

      <Subhead n={4} title="Where AI helps and where AI hurts on drift" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is useful around the re-prove move. It is not useful during it.
        That is the whole point.
      </p>
      <SplitGrid />

      <Subhead n={5} title="The drift conversation with yourself" className="mt-12" />
      <p className="mt-4 text-text/90">
        Once a month, ask yourself one question, honestly. If my AI access
        disappeared for two weeks, could I still do my work at a level I
        would defend in front of my team? If the answer is yes, you are
        using AI well. If the answer is no, you are drifting, and the move
        is not to panic. The move is to pick a re-prove task this week and
        rebuild the muscle.
      </p>
      <div className="mt-6 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          The rule
        </p>
        <p className="mt-3 text-text/90">
          One task a week, no AI, on purpose. The cost is small. The thing
          it protects is your judgment, and judgment is the anchor that
          everything else hangs on.
        </p>
      </div>
    </Section>
  );
}

const SIGNALS = [
  {
    n: '01',
    name: 'Empty-handed amnesia',
    body: 'A teammate asks about a doc you shipped last week. You cannot remember what you decided, only that the draft looked good. The reasoning never lived in your head. It lived in the chat session you closed.',
  },
  {
    n: '02',
    name: 'Tool-or-nothing',
    body: 'When the AI is slow, down, or unavailable, you stall. Not for one tricky step. For everything. The work feels impossible to start, even tasks you used to do without thinking. The dependency has become structural.',
  },
  {
    n: '03',
    name: 'Skipped reading',
    body: 'You used to read sources. Now you read AI summaries of sources and treat them as if you read the source. You cannot quote the actual article. You can only quote the summary, which is not the same thing.',
  },
  {
    n: '04',
    name: 'Defense gap',
    body: 'Asked about a sentence in your own work, you cannot defend it without going back to the chat. The sentence is yours by signature. It is not yours by judgment. That is a small drift signal that compounds over time.',
  },
  {
    n: '05',
    name: 'Conversation outsourcing',
    body: 'Messages that should come from you are being entirely drafted by AI. Not just polished. Authored. The voice in the inbox is no longer your voice. The relationship anchor (8.2) is being eroded one message at a time.',
  },
];

function SignalsGrid() {
  return (
    <div className="mt-6 space-y-4">
      {SIGNALS.map((p) => (
        <div key={p.n} className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-xs text-muted">Signal {p.n}</span>
            <h4 className="text-lg font-semibold text-amber">{p.name}</h4>
          </div>
          <p className="mt-3 text-text/90">{p.body}</p>
        </div>
      ))}
    </div>
  );
}

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Picking which task to re-prove this week (give it your last five and ask which one most needs your judgment).',
      'Comparing your no-AI version against an AI version after you finish, so you can see exactly where the gaps are.',
      'Drafting your one-sentence reflection if you do not know how to write the takeaway.',
      'Spotting drift signals in your own past work when you describe them to it honestly.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'During the re-prove task itself: the whole point is to do the work alone. Letting it in defeats the exercise.',
      'Convincing you the re-prove was pointless because the AI version was &lsquo;better.&rsquo; Faster is not the goal. Sharp is.',
      'Replacing the honest monthly question with a chipper progress summary. If you ask AI &lsquo;am I drifting,&rsquo; it will mostly say no.',
      'Auto-generating your operator notes: if you did not write the reflection yourself, the audit trail is fake.',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span dangerouslySetInnerHTML={{ __html: it }} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Spot the drift" title="Three scenes from real workflows. Each one shows two possible signals. Pick the one the scene actually demonstrates.">
      <p className="mt-6 text-text/90">
        Drift signals look similar from the outside. The skill is reading
        which one is actually showing up, so you know what to fix.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <DriftDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  scene: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    scene:
      'In standup on Monday, your team lead asks you to walk through the recommendation in the strategy memo you sent Friday. You open the doc, but you cannot remember why you landed on option 2 over option 3. You vaguely remember the AI laid it out well.',
    optionA: 'Empty-handed amnesia: the reasoning lived in the chat, not in you.',
    optionB: 'Skipped reading: you summarized instead of reading the sources.',
    correct: 'A',
    verdict:
      'Nothing in the scene points to skipping sources. The signal is that you authored the memo by signature but the reasoning behind option 2 over option 3 never moved into your head. That is empty-handed amnesia: the work shipped, your name is on it, but the judgment is in a chat you closed. The fix is not to write a longer memo. The fix is, before you ship next time, to write the one-paragraph version of your reasoning in your own words, with no AI in the loop.',
  },
  {
    n: '02',
    scene:
      'A coding-tool outage hits at 9am Wednesday. You have a small bug to fix that you would normally walk through with the assistant. Instead of opening the file and starting, you spend 45 minutes refreshing the status page and asking the team Slack whether anyone else is back online.',
    optionA: 'Conversation outsourcing: you are letting AI handle the team comms.',
    optionB: 'Tool-or-nothing: a small task you used to do solo now feels impossible to start without the tool.',
    correct: 'B',
    verdict:
      'There is no message-drafting in this scene, so conversation outsourcing does not fit. The actual signal is structural dependency. A bug you could have read and fixed in 20 minutes is now blocked, in your head, on a tool being back online. That is tool-or-nothing drift. The fix is the re-prove move: pick one small task this week and do it with the assistant closed. Prove to yourself you still know how.',
  },
  {
    n: '03',
    scene:
      'You are catching up on a long policy debate at your school or workplace. AI gives you a clean three-paragraph summary of the back-and-forth. You quote a line from the summary in a Slack message defending one side. A teammate asks &ldquo;where did that quote come from?&rdquo; You cannot say which actual document said it.',
    optionA: 'Skipped reading: you treated an AI summary as if you read the source.',
    optionB: 'Defense gap: you cannot defend a sentence you used.',
    correct: 'A',
    verdict:
      'Defense gap is close, but the deeper signal is skipped reading. The quote is not from a source you read. It is from a summary you treated as a source, which is a different thing. The summary is doing work the original document might not actually support. The fix is to never quote from a summary, only from a source you actually opened. AI summaries are great for picking what to read. They are not a substitute for reading it.',
  },
];

function DriftDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The scene
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: drill.scene }}
      />

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which drift signal is this?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {key}
                </span>
                <span className="mt-2 block" dangerouslySetInnerHTML={{ __html: text }} />
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That signal could be present too, but the scene gives more
              direct evidence for the other one. Read for the signal that
              the specific behavior in the scene demonstrates.
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The signal the scene actually shows' : 'Correct'} · Option {drill.correct}
            </p>
            <p className="mt-2 text-sm text-text/90" dangerouslySetInnerHTML={{ __html: drill.verdict }} />
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Drift in one sentence." answer="The quiet erosion of your judgment that happens when the AI gets good enough that you slowly stop doing the human parts of your own work." />
        <QuickCheckItem n="Q2" q="The five drift signals." answer="Empty-handed amnesia, tool-or-nothing, skipped reading, defense gap, conversation outsourcing." />
        <QuickCheckItem n="Q3" q="What is the re-prove move?" answer="Once a week, take one real task you would normally do with AI and do it without. Then compare. Then write one sentence on what you noticed." />
        <QuickCheckItem n="Q4" q="Two things AI helps with on drift. Two things AI hurts." answer="Helps: picking the re-prove task, comparing versions afterward, drafting reflections, spotting drift in past work. Hurts: doing the re-prove task itself, convincing you re-proving was pointless, replacing the honest monthly question, auto-generating your operator notes." />
        <QuickCheckItem n="Q5" q="The monthly drift question, in one line." answer="If my AI access disappeared for two weeks, could I still do my work at a level I would defend in front of my team?" />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. Drift signals and the re-prove move.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 8.3 · Lock card · Drift &amp; re-prove
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          The work still ships. The judgment behind it quietly goes soft.
          One task a week, no AI, keeps you sharp.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <LockTile n="01" name="Amnesia" body="Cannot recall why you shipped what you shipped." />
          <LockTile n="02" name="Tool-or-nothing" body="Outage breaks tasks you used to do solo." />
          <LockTile n="03" name="Skipped reading" body="Quoting summaries you treat as the source." />
          <LockTile n="04" name="Defense gap" body="Cannot defend a sentence without the chat open." />
          <LockTile n="05" name="Outsourced voice" body="Messages from you that you did not author." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The re-prove move
          </p>
          <p className="mt-2 text-text/90">
            One task a week, no AI, on purpose. Compare after. Write one
            sentence on what you noticed.
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            The monthly question
          </p>
          <p className="mt-2 text-text/90">
            If my AI access disappeared for two weeks, could I still do my
            work at a level I would defend? If no, pick a re-prove task
            this week.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          AI makes you faster when you stay sharp. It hollows you out when
          you let it carry the work.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 8.4
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Picking the right tool for the job you actually have
          </h3>
          <p className="mt-3 text-sm text-text/85">
            Most people pick AI tools by hype. Operators pick by fit. Vocab:
            Fit. The five tool categories worth knowing, the three-question
            fit test, and how to build a starter stack for the work you
            actually do.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text" dangerouslySetInnerHTML={{ __html: title }} />
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 8.3 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From 8.2',
    'Drift &amp; re-prove',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span dangerouslySetInnerHTML={{ __html: label }} />
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
